/**
 * Order quad corners into a non-self-intersecting ring by sorting them by
 * angle around the centroid.
 *
 * Detection parcels deliver corners row-major ([TL, TR, BL, BR]) while
 * user-drawn boxes use perimeter order ([TL, TR, BR, BL]); drawing a polygon
 * through row-major corners produces a self-intersecting "bowtie". Both
 * orders normalize to the same ring here. Arrays with fewer than 3 points
 * pass through unchanged.
 */
export const orderCorners = (points) => {
    if (points.length < 3)
        return points;
    const cx = points.reduce((sum, p) => sum + p[0], 0) / points.length;
    const cy = points.reduce((sum, p) => sum + p[1], 0) / points.length;
    return [...points].sort((a, b) => {
        const angleA = Math.atan2(a[1] - cy, a[0] - cx);
        const angleB = Math.atan2(b[1] - cy, b[0] - cx);
        if (angleA !== angleB)
            return angleA - angleB;
        // Deterministic tie-break for corners collinear with the centroid
        if (a[0] !== b[0])
            return a[0] - b[0];
        return a[1] - b[1];
    });
};
/**
 * True when the 4 corners form an axis-aligned rectangle: the distinct x
 * values cluster to at most 2 within epsilon, and likewise for y. Winding
 * independent. Also returns true for anything that isn't 4 points, so
 * irregular inputs fall back to the existing AABB rendering.
 */
export const isAxisAlignedQuad = (points, epsilon = 1e-3) => {
    if (points.length !== 4)
        return true;
    const clustersWithinTwo = (values) => {
        const clusters = [];
        for (const v of values) {
            if (!clusters.some((c) => Math.abs(c - v) <= epsilon)) {
                clusters.push(v);
                if (clusters.length > 2)
                    return false;
            }
        }
        return true;
    };
    return (clustersWithinTwo(points.map((p) => p[0])) &&
        clustersWithinTwo(points.map((p) => p[1])));
};
/**
 * Format points as an SVG <polygon> `points` attribute string, scaling each
 * coordinate (default ×100 to map normalized 0-1 coords into percent space).
 */
export const toSvgPolygonPoints = (points, scale = 100) => points.map((p) => `${p[0] * scale},${p[1] * scale}`).join(" ");
